import dayjs from 'dayjs';

import type { PurchaseOrder, PurchaseOrderSummary } from '../api/purchaseOrders';
import { formatAmount } from '../format/money';

/**
 * The purchase order screens' words (canvas "Purchase order flow", 2026-10-25): a register row's
 * "2 designs · 30 garments" and "3 days late", and the order's four steps — ordered, received,
 * billed, paid — each with where it stands and the one move that takes it on. Every count is the
 * server's; nothing here works out what came or what was billed.
 */

const rupees = (value: number) => `₹${formatAmount(value)}`;
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;
const day = (value: string) => dayjs(value.slice(0, 10)).format('D MMM YYYY');

/** "2 designs · 30 garments". */
export function orderSize(order: Pick<PurchaseOrderSummary, 'designCount' | 'orderedQuantity'>) {
  return `${plural(order.designCount, 'design')} · ${plural(order.orderedQuantity, 'garment')}`;
}

/**
 * "3 days late" while something is still expected and its day has gone; empty otherwise — a
 * received or ended order is never late.
 */
export function lateWords(
  order: Pick<PurchaseOrderSummary, 'expectedOn' | 'status'>,
  today: string,
): string {
  if (!order.expectedOn || (order.status !== 'Open' && order.status !== 'PartlyReceived')) {
    return '';
  }

  const days = dayjs(today.slice(0, 10)).diff(dayjs(order.expectedOn.slice(0, 10)), 'day');

  return days > 0 ? `${plural(days, 'day')} late` : '';
}

/** How far along a received bar runs, 0–100. */
export function receivedShare(
  order: Pick<PurchaseOrderSummary, 'orderedQuantity' | 'receivedQuantity'>,
) {
  return order.orderedQuantity <= 0
    ? 0
    : Math.min(100, Math.round((order.receivedQuantity / order.orderedQuantity) * 100));
}

export type StepState = 'done' | 'now' | 'later';

export type StepMove = 'receive' | 'bill' | 'pay';

export interface OrderStep {
  key: 'ordered' | 'received' | 'billed' | 'paid';
  title: string;
  line: string;
  state: StepState;
  /** The move that takes this step on, when there is one to make. */
  move: StepMove | null;
}

/** Each move's button — "Receive the rest" once something has come. */
export function moveWords(
  order: Pick<PurchaseOrder, 'receivedQuantity'>,
): Record<StepMove, string> {
  return {
    receive: order.receivedQuantity > 0 ? 'Receive the rest' : 'Receive stock',
    bill: 'Enter their bill',
    pay: 'Pay the supplier',
  };
}

/** What the order's bills still want paying, across the whole of each bill. */
export function stillToPay(order: Pick<PurchaseOrder, 'bills'>): number {
  return order.bills.reduce((sum, bill) => sum + Math.max(0, bill.billTotal - bill.paidAmount), 0);
}

/**
 * The four steps, in order. A cancelled order has none — nothing was ever going to come. `money`
 * leaves out the paid step for someone who may not see what bills come to.
 */
export function orderSteps(order: PurchaseOrder, money: boolean): OrderStep[] {
  if (order.status === 'Cancelled') {
    return [];
  }

  const { orderedQuantity: ordered, receivedQuantity: received, billedQuantity: billed } = order;
  const pending = order.lines.reduce((sum, line) => sum + line.pendingQuantity, 0);
  const deliveries =
    order.receipts.length === 1 ? '1 delivery' : `${order.receipts.length} deliveries`;
  const onBills =
    order.bills.length === 1
      ? ` on ${order.bills[0]!.documentNumber}`
      : order.bills.length > 1
        ? ` on ${plural(order.bills.length, 'bill')}`
        : '';

  const steps: OrderStep[] = [
    {
      key: 'ordered',
      title: 'Ordered',
      line: `${day(order.orderDate)} · ${plural(ordered, 'garment')}`,
      state: 'done',
      move: null,
    },
    {
      key: 'received',
      title: 'Received',
      line:
        received === 0
          ? 'Nothing has come yet'
          : `${received} of ${ordered} · ${order.status === 'ClosedShort' ? 'closed short' : deliveries}`,
      state: pending > 0 ? 'now' : 'done',
      move: pending > 0 ? 'receive' : null,
    },
    {
      key: 'billed',
      title: 'Billed',
      line: received === 0 ? 'Nothing to bill yet' : `${billed} of ${received}${onBills}`,
      state: received === 0 ? 'later' : billed >= received ? 'done' : 'now',
      move: received > billed ? 'bill' : null,
    },
  ];

  if (money) {
    const owed = stillToPay(order);

    steps.push({
      key: 'paid',
      title: 'Paid',
      line:
        order.bills.length === 0
          ? 'Nothing billed yet'
          : owed > 0
            ? `${rupees(owed)} still to pay`
            : 'Every bill paid',
      state: order.bills.length === 0 ? 'later' : owed > 0 ? 'now' : 'done',
      move: owed > 0 ? 'pay' : null,
    });
  }

  return steps;
}
